import { useState } from 'react';

import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { Badge, Box, Button, Group, Skeleton, Stack, Table, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { RoleAssignmentRow } from '@/components/RoleAssignmentRow';
import { useLegalEntityContext } from '@/context/LegalEntityContext';
import { staffApi } from '@/features/staff/data/staff-api';
import { STAFF_QUERY_KEYS } from '@/features/staff/data/query-keys';
import { useStaff } from '@/features/staff/data/useStaff';
import { CreateStaffModal } from '@/features/staff/modals/CreateStaffModal';
import { useUser } from '@/features/user/data/useUser';
import { buildHead } from '@/utils/html';

export const Route = createFileRoute('/_authenticated/company/staff/')({
  component: CompanyStaffPage,
  head: () => buildHead('staff'),
});

function CompanyStaffPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeEntity } = useLegalEntityContext();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [managerPopoverOpen, setManagerPopoverOpen] = useState(false);

  const { data: staff = [], isPending } = useStaff(activeEntity?.id ?? null, includeInactive);
  const { data: user } = useUser();
  const isGlobalAdmin = user?.roles.includes('GlobalAdmin') ?? false;

  const queryClient = useQueryClient();
  const setManagerMutation = useMutation({
    mutationFn: ({ staffId, isManager }: { staffId: string; isManager: boolean }) => staffApi.setManager(staffId, isManager),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: STAFF_QUERY_KEYS.all });
    },
  });

  const managers = staff.filter((s) => s.isManager).map((s) => ({ id: s.id, displayName: s.displayName }));
  const nonManagers = staff.filter((s) => !s.isManager && s.isActive).map((s) => ({ id: s.id, displayName: s.displayName }));

  if (!activeEntity) return null;

  return (
    <Box p='xl'>
      <Stack gap='xl'>
        <Group justify='space-between' align='center'>
          <Text fw={600} size='xs' c='charcoal.5' style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            {staff.length} {t('nav.staff').toLowerCase()}
          </Text>
          <Group gap='sm'>
            <Button
              variant={includeInactive ? 'filled' : 'subtle'}
              color='beige'
              size='xs'
              onClick={() => {
                setIncludeInactive((v) => !v);
              }}
            >
              {t('staff.showInactive')}
            </Button>
            <Button
              leftSection={<Plus size={14} />}
              size='xs'
              onClick={() =>
                modals.open({
                  title: t('staff.createTitle'),
                  children: <CreateStaffModal legalEntityId={activeEntity.id} />,
                })
              }
            >
              {t('staff.createButton')}
            </Button>
          </Group>
        </Group>

        {isGlobalAdmin && (
          <RoleAssignmentRow
            icon={ShieldCheck}
            label={t('staff.managers')}
            assigned={managers}
            available={nonManagers}
            opened={managerPopoverOpen}
            onToggleOpen={() => setManagerPopoverOpen((v) => !v)}
            onClose={() => setManagerPopoverOpen(false)}
            onAssign={(id) => {
              setManagerPopoverOpen(false);
              setManagerMutation.mutate({ staffId: id, isManager: true });
            }}
            onRemove={(id) => setManagerMutation.mutate({ staffId: id, isManager: false })}
            hideCreate
          />
        )}

        {isPending ? (
          <Skeleton height={20} radius='sm' />
        ) : staff.length === 0 ? (
          <Text c='charcoal.4'>{t('staff.noData')}</Text>
        ) : (
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('staff.columns.name')}</Table.Th>
                <Table.Th>{t('staff.columns.email')}</Table.Th>
                <Table.Th>{t('staff.columns.status')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {staff.map((s) => (
                <Table.Tr
                  key={s.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate({ to: '/company/staff/$staffId', params: { staffId: s.id } })}
                >
                  <Table.Td>
                    <Text size='sm' fw={500} c='charcoal.8'>
                      {s.displayName}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size='sm' c='charcoal.5'>
                      {s.email}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge size='xs' color={s.isActive ? 'teal' : 'beige'}>
                      {s.isActive ? t('staff.status.active') : t('staff.status.inactive')}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </Box>
  );
}
